/**
 * Quixo Audit list (Phase 1, G04/G05/G08): the workspace's audits from GET /v1/growth-audit/jobs (own audits plus website
 * orders linked to this workspace), audits paid for but not started yet (GET /v1/growth-audit/entitlements), and a
 * signed-in purchase form (POST /v1/billing/portal-checkout). Stripe returns here with `?order=` (and `&cancelled=1`).
 * Old links: `/audits/:id` (report-ready email) and `/audit/status/:id` redirect to the detail page.
 */
import { Link, Navigate, useParams, useSearchParams } from 'react-router';
import { Badge, Card, CardHead, DataTable, EmptyState, ErrorState, Loading, PageHeader, Text, type Column } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFlags } from '../../../lib/flags';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { useWorkspaceName } from '../_shared/ListScreen';
import { BuyAudit } from './BuyAudit';
import { GROWTH_AUDIT_MODULE_FLAG, GrowthAuditModuleCard } from './GrowthAuditModuleCard';
import { subjectLabel, tierKey, type AuditJob, type UnusedEntitlement } from './audit-view';
import './audit.css';

export const WRITE_ROLES = ['customer_owner', 'customer_admin', 'customer_member'];
export const canWrite = (roles: readonly string[] | undefined) => !!roles?.some((r) => WRITE_ROLES.includes(r));

export const statusTone = (s: AuditJob['status']) => (s === 'ready' ? 'success' : s === 'delayed' ? 'warning' : 'accent');

export function AuditPage() {
  const { t, me } = useApp();
  const f = useFormat();
  const workspace = useWorkspaceName();
  const [params] = useSearchParams();
  const writer = canWrite(me?.roles);
  const moduleOn = useFlags()(GROWTH_AUDIT_MODULE_FLAG) === 'on';
  const jobs = useResource<AuditJob[]>('/v1/growth-audit/jobs');
  const unused = useResource<UnusedEntitlement[]>(writer ? '/v1/growth-audit/entitlements' : null);
  const order = params.get('order');
  const cancelled = params.get('cancelled') === '1';

  return (
    <section className="qm-page qm-audit" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t('shell.screens.audit.title')} intro={t('shell.p1.list.intro')}
        actions={<Link className="qm-text-link" to="/audit/dashboard">{t('shell.screens.auditDash.open')}</Link>} />

      {order ? (
        <Card role="status" className="qm-audit__banner" data-tone={cancelled ? 'warning' : 'success'}>
          <Text>{t(cancelled ? 'shell.p1.list.cancelled' : 'shell.p1.list.orderReceived')}</Text>
        </Card>
      ) : null}

      {moduleOn ? <GrowthAuditModuleCard /> : null}

      {writer && unused.status === 'ready' && unused.data.length ? (
        <Card aria-labelledby="audit-start">
          <CardHead id="audit-start" title={t('shell.p1.start.title')} sub={t('shell.p1.start.sub')} />
          <ul className="qm-list">
            {unused.data.map((e) => {
              const k = tierKey(e.tier ?? e.sku);
              const q = new URLSearchParams({ entitlement: e.id, ...(k ? { tier: k } : {}), ...(e.website ? { website: e.website } : {}) });
              return (
                <li key={e.id} className="qm-list__item">
                  <span>{k ? t(`shell.p1.tier.${k}`) : ''}{e.website ? <> · <bdi>{f.text(subjectLabel(e.website).label)}</bdi></> : null}</span>
                  <Link className="qm-button" data-variant="primary" data-size="sm" to={`/audit/new?${q.toString()}`}>{t('shell.p1.start.cta')}</Link>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}

      <Card aria-labelledby="audit-list">
        <CardHead id="audit-list" title={t('shell.p1.list.title')} />
        {jobs.status === 'loading' ? <Loading label={t('common.loading')} /> : jobs.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : !jobs.data.length ? (
          <EmptyState title={t('shell.p1.list.empty')}><Text tone="muted">{t('shell.p1.list.emptyHint')}</Text></EmptyState>
        ) : (
          <DataTable caption={t('shell.p1.list.title')} columns={[
            { key: 'subject', header: t('shell.p1.col.subject'), render: (r) => <Link to={`/audit/${encodeURIComponent(r.id)}`}><bdi>{f.text(subjectLabel(r.targetUrl).label)}</bdi></Link> },
            { key: 'tier', header: t('shell.screens.auditDash.tierCol'), render: (r) => { const k = tierKey(r.tier); return k ? t(`shell.p1.tier.${k}`) : ''; } },
            { key: 'kind', header: t('shell.screens.auditDash.kind'), render: (r) => (r.rerunOf ? t('shell.screens.auditDash.kindRerun') : t('shell.screens.auditDash.kindAudit')) },
            { key: 'status', header: t('shell.screens.auditDash.status'), render: (r) => <Badge tone={statusTone(r.status)}>{t(`shell.screens.auditDash.status.${r.status}`)}</Badge> },
            { key: 'createdAt', header: t('shell.screens.auditDash.started'), render: (r) => f.date(r.createdAt) },
            { key: 'open', header: t('shell.p1.col.open'), render: (r) => <Link className="qm-text-link" to={`/audit/${encodeURIComponent(r.id)}`}>{t('shell.p1.list.view')}</Link> },
          ] satisfies Column<AuditJob>[]} rows={jobs.data} />
        )}
      </Card>

      {writer ? (
        <Card aria-labelledby="audit-buy">
          <CardHead id="audit-buy" title={t('shell.screens.audit.buy')} sub={t('shell.p1.buy.sub')} />
          <BuyAudit />
        </Card>
      ) : null}
    </section>
  );
}

/** `/audits/:id` (report-ready emails) and `/audit/status/:id` (old session status page) -> `/audit/:id`. */
export function AuditRedirect() {
  const { id = '' } = useParams();
  // an order id from the old status page goes to the list, where the order banner explains what happens next
  if (/^ord_/.test(id)) return <Navigate to={`/audit?order=${encodeURIComponent(id)}`} replace />;
  return <Navigate to={id ? `/audit/${encodeURIComponent(id)}` : '/audit'} replace />;
}

